/**
 * The documentation registry (DOCS-REFRESH, layer S).
 *
 * One typed list of every documentation page, in sidebar order. The sidebar,
 * the pager, the hub cards, the sitemap, llms.txt and each page's JSON-LD all
 * read from here, so a page exists in the navigation the moment it is
 * registered and nothing has to be kept in sync by hand.
 *
 * Every slug in PLAN.md's information architecture is registered now, before
 * its content is written, so the neighbours, the sitemap and the hub are
 * stable while writers fill the pages in parallel (C1–C3). A registered slug
 * with no `app/(site)/docs/<slug>/page.tsx` yet answers 404 through
 * app/(site)/docs/not-found.tsx; the content-standards spec lists which exist.
 *
 * Titles and descriptions are English (D-D1); the chrome around them —
 * group labels, "Updated", pager, callouts — comes from the `Docs.*`
 * namespace in all ten catalogues.
 *
 * ── Naming rule for product areas ──────────────────────────────────────
 * A product area is a proper noun and keeps its capital mid-sentence:
 * Council, Flows, Skills, Maps, Studio, Library, Marketplace. One item of
 * that area is a common noun and stays lowercase ("a flow", "two skills",
 * "an agent"). "chat", "agents", "projects" and "knowledge bases" are common
 * nouns everywhere except at the start of a sentence. This applies to the
 * descriptions here, the hub lead, llms.txt and the Open Graph card.
 */

export type DocGroupId =
  | "startHere"
  | "chat"
  | "agents"
  | "skills"
  | "council"
  | "flows"
  | "studio"
  | "maps"
  | "library"
  | "marketplace"
  | "billing"
  | "organization"
  | "mobile"
  | "developers";

/**
 * A page that is not on for every reader: behind a deployment flag (the
 * operator turns it on) or a plan (the organization pays for it). The sidebar
 * shows a pill either way; the copy comes from `Docs.gatedFlag` /
 * `Docs.gatedPlan`.
 */
export type DocGate = { flag: string } | { plan: string };

export interface DocPageInput {
  /** Path below `/docs/`, may contain one `/` for a child page (`chat/models-and-tools`). */
  slug: string;
  title: string;
  /** ≤158 characters — longer descriptions truncate in the SERP. */
  description: string;
  /** ISO date (YYYY-MM-DD) the content last changed; feeds dateModified and the "Updated" line. */
  updated: string;
  /** ISO date (YYYY-MM-DD) the page first went live; feeds datePublished / article:published_time. */
  published: string;
  keywords: readonly string[];
  gated?: DocGate;
}

export interface DocPage extends DocPageInput {
  href: string;
  group: DocGroupId;
}

export interface DocGroup {
  id: DocGroupId;
  pages: readonly DocPage[];
}

/** The hub itself — `/docs` — listed first under "Start here" but not part of the pager. */
export const DOCS_HUB = {
  href: "/docs",
  title: "Akili Documentation",
  description:
    "Guides for using Akili: chat across models, agents, Skills, Council, Flows, Studio, Maps, knowledge bases, billing and organization settings.",
  updated: "2026-10-12",
  published: "2026-07-12",
} as const;

export const DOC_DESCRIPTION_MAX_LENGTH = 158;

const UPDATED = "2026-10-12";
/** The refresh that wrote every page below except the two older guides. */
const PUBLISHED = "2026-10-12";
/** The first commit of the /docs section (779084ab): getting-started and the webchat guide date from it. */
const FIRST_DOCS_COMMIT = "2026-07-12";

const GROUPS: ReadonlyArray<{ id: DocGroupId; pages: readonly DocPageInput[] }> = [
  {
    id: "startHere",
    pages: [
      {
        slug: "getting-started",
        title: "Getting started with Akili",
        description:
          "Sign in, pick a model, ground a chat in your own documents, build a first agent and understand how credits and plans work — in about ten minutes.",
        updated: UPDATED,
        published: FIRST_DOCS_COMMIT,
        keywords: ["getting started", "quickstart", "first chat", "model picker", "credits"],
      },
      {
        slug: "what-you-can-do",
        title: "What you can do with Akili",
        description:
          "A tour of the platform: multi-model chat, agents, Skills, Council, Flows, Studio, Maps, Library, Marketplace, billing, organization and mobile.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["platform tour", "features", "overview", "AI workspace"],
      },
    ],
  },
  {
    id: "chat",
    pages: [
      {
        slug: "chat",
        title: "Chat: conversations and history",
        description:
          "Start conversations, switch models mid-thread, organize chats into folders and projects, branch a thread and share a conversation.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["chat", "conversations", "folders", "branching", "share a chat"],
      },
      {
        slug: "chat/models-and-tools",
        title: "Models and tools in chat",
        description:
          "The model picker, web search, deep research, extended thinking, voice, attachments, memory and artifacts — what each does and what it costs.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["models", "web search", "deep research", "thinking", "voice", "attachments", "memory", "artifacts"],
      },
    ],
  },
  {
    id: "agents",
    pages: [
      {
        slug: "agents",
        title: "Agents: build a custom assistant",
        description:
          "Create a custom agent with instructions, a model, tools and knowledge; test it beside the form, export it as a zip, share it privately or publish it.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["agents", "system prompt", "versioning", "custom assistant"],
      },
      {
        slug: "agents/knowledge-and-connectors",
        title: "Knowledge and connectors for agents",
        description:
          "Attach knowledge bases, MCP servers and app connectors to an agent so it can answer from your documents and act in the tools you already use.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["agent knowledge", "MCP", "connectors", "tools", "RAG"],
      },
      {
        slug: "agents/share-and-publish",
        title: "Share and publish an agent",
        description:
          "Share an agent with your organization, give it a public page, deploy it as webchat, route it to an inbox or expose it to other agents over A2A.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["share agent", "public agent page", "webchat", "inbox", "A2A", "agent-to-agent"],
      },
      {
        slug: "webchat-widget-embed",
        title: "Embed the webchat widget",
        description:
          "Add the Akili chat bubble to any website with one script tag: configuration, verified visitor identity, the host-page API and language settings.",
        updated: UPDATED,
        published: FIRST_DOCS_COMMIT,
        keywords: ["webchat", "embed", "widget", "script tag", "identity hash", "window.Akili"],
      },
    ],
  },
  {
    id: "skills",
    pages: [
      {
        slug: "skills",
        title: "Skills: reusable procedures for agents",
        description:
          "Reusable instruction packs an agent loads on demand: write one, test it, submit it for review and attach it to the agents that need it.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["skills", "agent skills", "instruction packs", "review"],
        gated: { flag: "AGENT_SKILLS" },
      },
    ],
  },
  {
    id: "council",
    pages: [
      {
        slug: "council",
        title: "Council: several models, one answer",
        description:
          "Ask several models the same question, let an adjudicator weigh their answers and read one synthesis with each member's verdict.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["council", "multi-model", "adjudicator", "synthesis", "compare models"],
        gated: { plan: "Business" },
      },
    ],
  },
  {
    id: "flows",
    pages: [
      {
        slug: "flows",
        title: "Flows: automate work across apps",
        description:
          "Automate work across agents, tools and apps: build a flow on a canvas, test each step, publish it, and let a schedule, webhook, event or agent run it.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["flows", "automation", "workflow builder", "triggers"],
        gated: { flag: "FLOWS_ENABLED" },
      },
      {
        slug: "flows/triggers-and-nodes",
        title: "Flow triggers and nodes",
        description:
          "Every trigger (schedule, webhook, event, agent, manual) and every node family a flow can run, the template syntax between steps, and runs-as.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["flow triggers", "flow nodes", "schedule", "webhook", "node configuration"],
        gated: { flag: "FLOWS_ENABLED" },
      },
      {
        slug: "flows/runs-and-approvals",
        title: "Flow runs and approvals",
        description:
          "Follow a run step by step, cancel or replay it, decide approvals from the inbox or a chat card, read failures and auto-pause, and use the API scopes.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["flow runs", "approvals", "retry", "run history"],
        gated: { flag: "FLOWS_ENABLED" },
      },
    ],
  },
  {
    id: "studio",
    pages: [
      {
        slug: "studio",
        title: "Studio: decks, documents and designs",
        description:
          "Make decks, documents, web pages, social posts and designs from a brief and your sources, then remix, theme, comment, convert, export and share them.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["studio", "design canvas", "slides", "export", "AI images"],
      },
      {
        slug: "studio/import-and-templates",
        title: "Studio import and templates",
        description:
          "Import PowerPoint, Word, PDF and Penpot files with a fidelity report, browse the template gallery, try a template with your content, and save your own.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["import PowerPoint", "pptx", "templates", "studio templates"],
      },
      {
        slug: "studio/publish",
        title: "Publish from Studio",
        description:
          "Connect ten social networks, compose and preview per channel, schedule through review, read results, and what the v3 and short-link flags add.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["publish", "social media", "schedule posts", "client review", "LinkedIn", "X"],
      },
    ],
  },
  {
    id: "maps",
    pages: [
      {
        slug: "maps",
        title: "Maps: places and routes",
        description:
          "Ask for places, hours, routes and street-level photos in a map chat, share or type a location, filter by category and radius, and export a plan.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["maps", "places", "directions", "street view", "location"],
      },
    ],
  },
  {
    id: "library",
    pages: [
      {
        slug: "library",
        title: "Library: files and collections",
        description:
          "Upload files, pass the duplicate and safety checks, group them into collections, share by email, link or team, and feed them to chat and knowledge bases.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["library", "files", "collections", "sharing", "storage"],
      },
      {
        slug: "knowledge-bases",
        title: "Knowledge bases: grounded answers",
        description:
          "Upload documents or connect sources, let Akili index them, and get grounded answers with citations in chat and from agents.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["knowledge base", "RAG", "citations", "upload documents", "indexing"],
      },
      {
        slug: "projects",
        title: "Projects: shared context for work",
        description:
          "Group chats, files and instructions around one piece of work, invite collaborators with roles and share a project by link.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["projects", "project instructions", "collaborators", "share a project"],
      },
    ],
  },
  {
    id: "marketplace",
    pages: [
      {
        slug: "marketplace",
        title: "Marketplace: find and install agents",
        description:
          "Find and install agents, integrations and knowledge packs from other builders, manage what you installed and publish your own listing.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["marketplace", "install agent", "listings", "publisher"],
      },
      {
        slug: "apps-and-connectors",
        title: "Apps and connectors",
        description:
          "Connect GitHub, Linear, Jira, Notion, Asana, Figma, Sentry and Stripe with one click so chats and agents can read from and act in them, with your consent.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["connectors", "apps", "OAuth", "GitHub", "Linear", "Jira", "Notion", "MCP"],
      },
    ],
  },
  {
    id: "billing",
    pages: [
      {
        slug: "plans-and-billing",
        title: "Plans and billing",
        description:
          "What each plan includes, how seats and pooled credits work for teams, how to upgrade, change or cancel, and where invoices live.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["plans", "billing", "pricing", "seats", "upgrade", "invoices"],
      },
      {
        slug: "credits-and-usage",
        title: "Credits and usage",
        description:
          "What a credit is, which features spend credits and how they are metered, how to read the usage ledger, what happens at zero, and how to add credits.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["credits", "usage", "metering", "credit packs", "spend cap"],
      },
      {
        slug: "coupons-and-trials",
        title: "Coupons and trials",
        description:
          "Enter a promo code at checkout, percent and amount discounts, free-trial codes with or without a card, intro offers, license codes and why a code is refused.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["coupons", "promo code", "trial", "discount", "checkout"],
      },
    ],
  },
  {
    id: "organization",
    pages: [
      {
        slug: "organization",
        title: "Organization: workspaces and roles",
        description:
          "Create a team workspace, invite members with invitations or codes, assign roles, set policies, bring your own provider keys, webhooks, analytics and audit.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["organization", "team workspace", "roles", "policies", "BYOK", "invite members"],
      },
      {
        slug: "sso-and-domains",
        title: "SSO and domains",
        description:
          "Connect Okta, Entra, Google Workspace or any OIDC or SAML provider, verify your domain, require SSO, SCIM provisioning, and what members see at sign-in.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["SSO", "SAML", "OIDC", "verified domain", "enterprise sign-in"],
      },
      {
        slug: "security-and-account",
        title: "Security and account",
        description:
          "Sign-in methods, passkeys, two-factor, sessions and trusted devices, language and theme, memory and context controls, API keys and deleting your account.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["security", "two-factor", "passkeys", "sessions", "API keys", "delete account"],
      },
    ],
  },
  {
    id: "mobile",
    pages: [
      {
        slug: "mobile",
        title: "Mobile: Akili on iOS and Android",
        description:
          "Akili on iOS and Android: how to sign in, chat by voice, approve flows, reach your knowledge, Library and projects, and what the app does offline.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["mobile app", "iOS", "Android", "voice", "camera"],
      },
    ],
  },
  {
    id: "developers",
    pages: [
      {
        slug: "api",
        title: "API overview: keys, webhooks and MCP",
        description:
          "API keys and scopes, where the endpoint reference lives, signed organization webhooks, A2A agent discovery, MCP, rate limits and provider keys.",
        updated: UPDATED,
        published: PUBLISHED,
        keywords: ["API", "REST", "API key", "developers", "SSE", "integration"],
      },
    ],
  },
];

export function docHref(slug: string): string {
  return `/docs/${slug}`;
}

/** The groups in sidebar order, every page carrying its href and group. */
export const docGroups: readonly DocGroup[] = GROUPS.map(({ id, pages }) => ({
  id,
  pages: pages.map((page) => ({ ...page, href: docHref(page.slug), group: id })),
}));

const ALL_PAGES: readonly DocPage[] = docGroups.flatMap((group) => group.pages);
const BY_SLUG = new Map(ALL_PAGES.map((page) => [page.slug, page]));

/** Every registered page in reading order (the pager's order). */
export function allDocPages(): readonly DocPage[] {
  return ALL_PAGES;
}

export function docPage(slug: string): DocPage | undefined {
  return BY_SLUG.get(slug);
}

export function docGroupOf(slug: string): DocGroup | undefined {
  const page = BY_SLUG.get(slug);
  return page ? docGroups.find((group) => group.id === page.group) : undefined;
}

/** Previous and next page in reading order, across group boundaries. */
export function docNeighbours(slug: string): { prev: DocPage | null; next: DocPage | null } {
  const index = ALL_PAGES.findIndex((page) => page.slug === slug);
  if (index === -1) return { prev: null, next: null };
  return {
    prev: index > 0 ? (ALL_PAGES[index - 1] ?? null) : null,
    next: index < ALL_PAGES.length - 1 ? (ALL_PAGES[index + 1] ?? null) : null,
  };
}

/** 1-based position within the page's own group, for the "n of N" line. */
export function docPosition(slug: string): { index: number; total: number } | undefined {
  const group = docGroupOf(slug);
  if (!group) return undefined;
  return { index: group.pages.findIndex((page) => page.slug === slug) + 1, total: group.pages.length };
}

/** The newest date in the catalog — the hub is as fresh as its freshest page. */
export function docsLastUpdated(): string {
  return [DOCS_HUB.updated, ...ALL_PAGES.map((page) => page.updated)].sort().at(-1) ?? DOCS_HUB.updated;
}
